/// <amd-module name="VSSUI/Components/Table/ColumnDragDropBehavior" />
import * as React from "react";
import { BoltListDragEvent, IDragDroppableUI, IListDragDropSecondaryData, IOverlayRenderProps } from "VSSUI/Components/List/List.Props";
import { IListDragDropBehaviorOptions } from "VSSUI/Components/List/ListDragDropBehavior";
import { ListDropIndicatorPosition } from "VSSUI/Components/List/ListDropIndicator.Props";
import { cellFromEvent } from "VSSUI/List";
import { ITableColumn } from "VSSUI/Table";
import { getPointByEventType } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { beginDragOperation, DragDropEffect, DragImage, IDragDropOperation } from "VSSUI/Utilities/DragDrop";
import { ListDropIndicator } from "../List/ListDropIndicator";
import { ColumnFillId } from "./Table";

interface IDragImageData {
    image: React.ReactNode;
    xOffset?: number;
    yOffset?: number;
}

export interface ColumnDragDropBehaviorOptions extends IListDragDropBehaviorOptions<ITableColumn<{}>> {
    columns: Array<ITableColumn<{}>>;
}

/**
 * A behavior for dragging & dropping columns
 */
export class ColumnDragDropBehavior implements IBehavior<{}, IDragDroppableUI> {
    private eventDispatch: IEventDispatch;
    private options: ColumnDragDropBehaviorOptions;
    private dragImageData: IDragImageData | undefined;
    private dragDroppableUI: IDragDroppableUI;
    private operation: IDragDropOperation | undefined;
    private indicatorName: string;
    private listIndicatorPosition: ListDropIndicatorPosition;
    private contextMenuIndex: number;

    constructor(options: ColumnDragDropBehaviorOptions) {
        this.options = options;
        this.contextMenuIndex = options.columns.findIndex(col => col.id === "_more");
    }

    public initialize = (props: any, dragDroppableUI: IDragDroppableUI, eventDispatch: IEventDispatch) => {
        this.dragDroppableUI = dragDroppableUI;
        this.eventDispatch = eventDispatch;
        this.eventDispatch.addEventListener("pointerdown", this.onPointerDown);
        this.eventDispatch.addEventListener("dragstart", this.onDragStart);
        this.eventDispatch.addEventListener("dragend", this.onDragEnd);
        this.eventDispatch.addEventListener("dragenter", this.onDragEnter);
        this.eventDispatch.addEventListener("dragexit", this.onDragExit);
        this.eventDispatch.addEventListener("dragover", this.onDragOver);
        this.eventDispatch.addEventListener("drop", this.onDrop);

        this.indicatorName = "drop-indicator";
    };

    public componentWillUnmount() {
        this.eventDispatch?.removeEventListener("pointerdown", this.onPointerDown);
        this.eventDispatch?.removeEventListener("dragstart", this.onDragStart);
        this.eventDispatch?.removeEventListener("dragend", this.onDragEnd);
        this.eventDispatch?.removeEventListener("dragenter", this.onDragEnter);
        this.eventDispatch?.removeEventListener("dragexit", this.onDragExit);
        this.eventDispatch?.removeEventListener("dragover", this.onDragOver);
        this.eventDispatch?.removeEventListener("drop", this.onDrop);
    }

    public updateBehaviorOptions(options: ColumnDragDropBehaviorOptions) {
        this.options = options;
    }

    private beginDrag(event: React.PointerEvent<HTMLElement>) {
        const index = eventIsOnHeader(event);
        if (index !== null && this.options?.columns && index >= 0) {
            const item = this.options.columns[index];
            this.operation = beginDragOperation(event, {
                data: item,
                dropEffect: DragDropEffect.none,
                secondaryData: { index: index, sourceId: this.options.id } as IListDragDropSecondaryData,
                setDragImage: this.setDragImage,
                type: this.options.type
            });
        }
    }

    private onDragEnd = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        const index = eventIsOnHeader(event);

        if (index && index >= 0 && this.options.onDragEnd) {
            this.options.onDragEnd(event);
        }
        this.dragDroppableUI.removeOverlay("drag-source-item");
        this.dragImageData = undefined;
    };

    private onDragStart = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        if (event.detail.dataTransfer && !event.defaultPrevented) {
            const index = eventIsOnHeader(event);

            if (index !== null && index >= 0) {
                if (this.options.onDragStart) {
                    this.options.onDragStart(event);
                }
                if (event.detail.dataTransfer.effectAllowed !== DragDropEffect.none) {
                    this.dragDroppableUI.addOverlay(
                        "drag-source-item",
                        -1,
                        this.renderDragSourceItemOverlay,
                        /** z-index */ 0,
                        /** columnIndex */ index
                    );
                    if (this.dragImageData === undefined) {
                        this.dragImageData = {
                            image: this.options.renderDragImage(event)
                        };
                    }
                }
            }
        }
    };

    private onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
        if (event.button === 0) {
            this.beginDrag(event);
        }
    };

    private renderDragSourceItemOverlay = (props: IOverlayRenderProps) => {
        return (
            <>
                <div className="bolt-list-drag-source-item flex-grow" />
                {this.operation && this.dragImageData && <DragImage operation={this.operation}>{this.dragImageData.image}</DragImage>}
            </>
        );
    };

    private setDragImage = (image: React.ReactNode, xOffset?: number, yOffset?: number) => {
        this.dragImageData = { image: image, xOffset: xOffset, yOffset: yOffset };
    };

    private handlesType(event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>): boolean {
        const type = event.detail.dataTransfer?.type || "";
        return this.options.allowedTypes.indexOf(type) !== -1;
    }

    private onDragEnter = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        if (!this.handlesType(event)) {
            return;
        }

        if (this.options.onDragEnter) {
            this.options.onDragEnter(event);
        } else {
            event.detail.dataTransfer.dropEffect = DragDropEffect.move;
        }
    };

    private onDragExit = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        if (!this.handlesType(event)) {
            return;
        }

        if (this.options.onDragExit) {
            this.options.onDragExit(event);
        }
        this.dragDroppableUI.removeOverlay(this.indicatorName);
    };

    private onDragOver = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        if (!this.handlesType(event)) {
            return;
        }

        const index = this.calculateIndex(event);
        const dragIndex = event.detail.dataTransfer.secondaryData!.index;
        const listId = event.detail.dataTransfer.secondaryData!.sourceId;

        if (index >= 0 && (index !== dragIndex || listId !== this.options.id) && this.options.columns[index].id !== ColumnFillId) {
            if (this.options.onDragOver) {
                this.options.onDragOver(event, { index: this.listIndicatorPosition === ListDropIndicatorPosition.right ? index + 1 : index });
            } else {
                event.detail.dataTransfer.dropEffect = DragDropEffect.move;
            }
        } else {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }

        if (event.detail.dataTransfer.dropEffect === DragDropEffect.none) {
            this.dragDroppableUI.removeOverlay(this.indicatorName);
        } else {
            this.dragDroppableUI.addOverlay(this.indicatorName, -1, this.renderDropIndicator, /** z-index */ 0, /** columnIndex */ index);
        }
    };

    private onDrop = (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>) => {
        if (!this.handlesType(event)) {
            return;
        }

        const index = this.calculateIndex(event);
        const dragIndex = event.detail.dataTransfer.secondaryData!.index;
        const listId = event.detail.dataTransfer.secondaryData!.sourceId;

        if (index >= 0 && (index !== dragIndex || listId !== this.options.id) && this.options.onDrop) {
            event.persist();
            this.options.onDrop(event, { index: this.listIndicatorPosition === ListDropIndicatorPosition.right ? index + 1 : index });
        }
        this.dragDroppableUI.removeOverlay(this.indicatorName);
    };

    private renderDropIndicator = (props: IOverlayRenderProps) => {
        const xOffset = this.listIndicatorPosition === ListDropIndicatorPosition.right ? props.rowElement.offsetWidth : 0;
        return <ListDropIndicator position={this.listIndicatorPosition} xOffset={xOffset - 3} lineOffset={xOffset} />;
    };

    private calculateIndex(event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>): number {
        const cell = cellFromEvent(event);
        let index = cell.cellIndex;

        if (cell.cellElement && event.detail.dataTransfer.secondaryData) {
            const dragIndex = event.detail.dataTransfer.secondaryData.index;
            const nativeEvent = event.detail.nativeEvent;
            const columnRect = cell.cellElement!.getBoundingClientRect();

            const point = getPointByEventType(nativeEvent);
            const leftOfColumn = point ? point.x < columnRect.width / 2 + columnRect.left : index < dragIndex;

            if (index < dragIndex) {
                this.listIndicatorPosition = ListDropIndicatorPosition.left;
                if (!leftOfColumn) {
                    index++;
                }

                if (index === this.contextMenuIndex) {
                    index--;
                }
            } else if (index > dragIndex) {
                this.listIndicatorPosition = ListDropIndicatorPosition.right;
                if (leftOfColumn) {
                    index--;
                }

                if (index + 1 < this.options.columns.length && index + 1 == this.contextMenuIndex) {
                    index++;
                }
            }
            // No-op if index === dragIndex
        }
        return index;
    }
}

function eventIsOnHeader(event: React.SyntheticEvent<HTMLElement, Event>) {
    const cell = cellFromEvent(event);
    return cell.rowIndex === -1 ? cell.cellIndex : null;
}
